import { useEffect, useState } from "react";
import { api, formatMoney } from "@/lib/api";
import { FileDown, Receipt, Wallet, Heart, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { generateReceiptPDF } from "@/lib/receiptPdf";
import { useBranding } from "@/context/BrandingContext";

const SETTLEMENT_LABEL = {
  cobrado: { label: "Cobrado", icon: Wallet, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  cxc:      { label: "CxC",     icon: AlertCircle, color: "text-amber-700 bg-amber-50 border-amber-200" },
  cortesia: { label: "Cortesía", icon: Heart, color: "text-purple-700 bg-purple-50 border-purple-200" },
};

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function PatientReceiptsList({ patientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { branding } = useBranding();

  useEffect(() => {
    let ok = true;
    setLoading(true);
    api.get(`/patients/${patientId}/receipts`)
      .then((r) => { if (ok) setItems(r.data || []); })
      .catch(() => { if (ok) setItems([]); })
      .finally(() => { if (ok) setLoading(false); });
    return () => { ok = false; };
  }, [patientId]);

  const download = (r) => {
    try {
      generateReceiptPDF({
        patient: r.patient,
        appointment: r.appointment || { start: r.created_at },
        evolucion: {
          diente: "",
          diagnostico: "",
          procedimiento: r.evolution_summary || "—",
        },
        items: r.items || [],
        settlement: r.settlement_type,
        payments: r.payments || [],
        applied: { anticipo: r.applied_anticipo || 0, credit: r.applied_credit || 0 },
        total: r.total_charged || 0,
        ar: r.ar_id ? { id: r.ar_id } : null,
        courtesy: r.settlement_type === "cortesia" ? { reason: r.courtesy_reason, notes: "" } : null,
        doctor: r.doctor || null,
        doctorName: r.doctor?.name || "",
        branding,
        receiptNumber: r.number,
      });
    } catch (e) {
      toast.error("No se pudo generar el PDF");
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground p-6 text-center">Cargando recibos…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border rounded-sm">
        <Receipt className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <div className="font-semibold text-sm mb-1">Sin recibos aún</div>
        <div className="text-xs text-muted-foreground">Los recibos se generan al finalizar una cita con cobro, CxC o cortesía.</div>
      </div>
    );
  }

  const total = items.reduce((a, r) => a + (Number(r.total_charged) || 0), 0);

  return (
    <div className="space-y-4" data-testid="patient-receipts">
      {/* Resumen */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Recibos" value={items.length} />
        <StatCard label="Total facturado" value={formatMoney(total)} />
        <StatCard
          label="Cobrado"
          value={formatMoney(items.filter((r) => r.settlement_type === "cobrado").reduce((a, r) => a + (r.total_charged || 0), 0))}
        />
        <StatCard
          label="Pendientes"
          value={items.filter((r) => r.settlement_type === "cxc").length}
          suffix="CxC"
        />
      </div>

      {/* Tabla */}
      <div className="border border-border rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-muted/60 label-eyebrow border-b border-border">
          <div className="col-span-2">Recibo</div>
          <div className="col-span-3">Fecha</div>
          <div className="col-span-2">Tipo</div>
          <div className="col-span-3">Procedimiento</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1" />
        </div>
        {items.map((r) => {
          const meta = SETTLEMENT_LABEL[r.settlement_type] || { label: r.settlement_type, icon: Receipt, color: "text-muted-foreground bg-muted border-border" };
          const Icon = meta.icon;
          return (
            <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border last:border-b-0 items-center hover:bg-muted/40 transition text-sm" data-testid={`receipt-row-${r.number}`}>
              <div className="col-span-2 font-mono font-bold">{r.number}</div>
              <div className="col-span-3 text-muted-foreground text-xs">{fmtDT(r.created_at)}</div>
              <div className="col-span-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest border ${meta.color}`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
              </div>
              <div className="col-span-3 truncate text-xs" title={r.evolution_summary}>
                {r.evolution_summary || "—"}
              </div>
              <div className="col-span-1 text-right font-bold">{formatMoney(r.total_charged || 0)}</div>
              <div className="col-span-1 flex justify-end">
                <button
                  onClick={() => download(r)}
                  className="p-2 rounded-sm text-muted-foreground hover:text-primary hover:bg-primary/10"
                  title="Descargar PDF"
                  data-testid={`receipt-pdf-${r.number}`}
                >
                  <FileDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix }) {
  return (
    <div className="border border-border rounded-sm p-3 bg-card">
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-display font-bold text-lg tracking-tight">
        {value} {suffix && <span className="text-xs text-muted-foreground font-normal">{suffix}</span>}
      </div>
    </div>
  );
}
